import _ from 'lodash';
import { CrossFrameUtils } from '@src/utils/CrossFrameUtils';

export interface IInterruptData {
    message?: string;
    yesCallback?: () => void;
    noCallback?: () => void;
    snoozeCallback?: () => void;
}

export class InterruptService {
    private static _interruptData: IInterruptData = {};

    /**
     * Sets up the appropriate callbacks into interruptData to be used by atInterruptDirective,
     * and then triggers atInterruptDirective with the interruptMessage.
     *
     * When calling interrupt, passing a falsey value as one of the below callbacks will disable that
     * callback and prevent that button from being displayed in the modal, allowing caller to control
     * what options are shown when the interrupt modal is created.
     *
     * @return {Promise} Resolves when modal is actioned on by associate, or rejected when new modal surfaces.
     *
     * @param interruptMessage: string
     * @param yesCallback: () => void
     * @param noCallback: () => void
     * @param snoozeCallback: () => void
     */
    public static async interrupt(interruptMessage: string, yesCallback?: () => void, noCallback?: () => void, snoozeCallback?: () => void) {
        this._interruptData.yesCallback = yesCallback;
        this._interruptData.noCallback = noCallback;
        this._interruptData.snoozeCallback = snoozeCallback;
        this.setCallbackListeners();
        return this.triggerInterrupt(interruptMessage);
    }

    /**
     * Overwrites interruptData with the default(empty) interrupt info. This resets the interrupt state,
     * closes the modal, and resolves the existing deferObject Promise.
     *
     * This function is called by atInterruptDirective after the selected modal callback function completes.
     */
    public static async clearInterrupt() {
        this._interruptData = {};
        return CrossFrameUtils.sendMessageToOtherFrame({
            frame: window,
            message: {
                method: 'clearInterrupt',
            },
        });
    }

    /**
     * Retrieves the interruptData that currently exists within atInterruptService.
     * Called by atInterruptDirective to watch for changes in the interruptMessage and to fetch the
     * callback functions that will be linked to the modal when the modal is displayed.
     *
     * @return {object} Current properties in atInterruptService (message and callback functions).
     */
    public static async getInterruptData() {
        return CrossFrameUtils.sendMessageToOtherFrame({
            frame: window,
            message: {
                method: 'getInterruptData',
            },
        });
    }

    // Private functions

    /**
     * atInterruptDirective has an eventHandle that watches for changes to interruptData.message, and will
     * trigger the interrupt modal when the message changes to a non-empty string.
     *
     * Rejects any existing promise/modal because a new interrupt should replace any currently open ones.
     * Only sets interruptData.message if the interruptMessage parameter is a non-empty String. Otherwise will
     * reject the promise and log the invalid parameter that was passed in.
     *
     * @return {Promise} Resolves when callbacks complete and clearInterrupt is called, or rejected when
     *  a new interrupt is triggered while current Promise is still active.
     * @param interruptMessage: string
     */
    private static async triggerInterrupt(interruptMessage: string) {
        if (_.isString(interruptMessage) && !_.isEmpty(interruptMessage)) {
            this._interruptData.message = interruptMessage;
            const payload = {
                message: this._interruptData.message,
                yesCallback: !!this._interruptData.yesCallback,
                noCallback: !!this._interruptData.noCallback,
                snoozeCallback: !!this._interruptData.snoozeCallback,
            };
            return CrossFrameUtils.sendMessageToOtherFrame({
                frame: window,
                message: {
                    method: 'setInterrupt',
                    data: payload,
                },
            });
        } else {
            this._interruptData.message = '';
            const consoleMsg = 'Must pass a non-empty string to atInterrupt. Attempted to pass in: ' + interruptMessage;
            console.warn(consoleMsg);
            return Promise.reject(consoleMsg);
        }
    }

    private static setCallbackListeners() {
        CrossFrameUtils.registerListener('interruptYesCallback', async () => {
            if (this._interruptData.yesCallback) {
                this._interruptData.yesCallback();
            }
            return Promise.resolve();
        });
        CrossFrameUtils.registerListener('interruptNoCallback', async () => {
            if (this._interruptData.noCallback) {
                this._interruptData.noCallback();
            }
            return Promise.resolve();
        });
        CrossFrameUtils.registerListener('interruptSnoozeCallback', async () => {
            if (this._interruptData.snoozeCallback) {
                this._interruptData.snoozeCallback();
            }
            return Promise.resolve();
        });
    }
}
